import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { checkSignInRateLimit } from '../utils/rateLimitService';
import { withTimeout } from '../utils/withTimeout';
import { getAuthErrorMessage } from '../utils/authErrorMessage';
import { ensureUserProfile } from '../utils/ensureUserProfile';
import GoogleSignInButton from './GoogleSignInButton';
import versionData from '../version.json';

// Sign-in waits on an App Check token. If that token never arrives the request
// is never sent and the form would sit disabled indefinitely with nothing to
// show, so bound it and surface a real error instead.
const SIGN_IN_TIMEOUT_MS = 25000;
const TIMEOUT_MESSAGE =
  'Połączenie z serwerem trwa zbyt długo. Sprawdź połączenie z internetem i spróbuj ponownie.';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const limitCheck = await checkSignInRateLimit(email);
      if (!limitCheck.allowed) {
        setError(
          limitCheck.message ||
            'Zbyt wiele prób logowania. Spróbuj ponownie później.'
        );
        return;
      }

      const credential = await withTimeout(
        signInWithEmailAndPassword(auth, email, password),
        SIGN_IN_TIMEOUT_MS,
        TIMEOUT_MESSAGE
      );
      await ensureUserProfile(credential.user);
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(
        getAuthErrorMessage(err, 'Nie udało się zalogować. Spróbuj ponownie.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-md w-full space-y-5">
        <div className="text-center">
          <img
            src="/icon-192x192.png"
            alt="ScrapYard Logo"
            className="mx-auto h-14 w-14 object-contain rounded-lg shadow-lg"
          />
          <h2 className="mt-3 text-2xl font-bold text-gray-900">
            Zaloguj się do konta
          </h2>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-100 border-l-4 border-red-500 rounded">
                {error}
              </div>
            )}
            <div className="space-y-1">
              <label
                className="block text-gray-700 text-sm font-medium"
                htmlFor="email"
              >
                Email
              </label>
              <input
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition duration-200"
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="Wprowadź swój email"
                autoComplete="username"
              />
            </div>
            <div className="space-y-1">
              <label
                className="block text-gray-700 text-sm font-medium"
                htmlFor="password"
              >
                Hasło
              </label>
              <input
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition duration-200"
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Wprowadź swoje hasło"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-700 hover:bg-orange-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-base font-semibold py-2.5 px-6 rounded-lg transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
          <div className="mt-4 flex items-center">
            <div className="flex-grow border-t border-gray-300" />
            <span className="mx-4 text-sm text-gray-500">lub</span>
            <div className="flex-grow border-t border-gray-300" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton
              label="Zaloguj się przez Google"
              onSuccess={() => navigate('/dashboard')}
              onError={setError}
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Kontynuując przez Google, akceptujesz{' '}
              <Link to="/terms" className="underline hover:text-gray-700">
                regulamin
              </Link>{' '}
              i{' '}
              <Link to="/privacy" className="underline hover:text-gray-700">
                politykę prywatności
              </Link>
              .
            </p>
          </div>
          <p className="mt-4 text-center text-sm text-gray-600">
            Nie masz konta?{' '}
            <Link
              to="/register"
              className="text-orange-700 hover:text-orange-800 font-medium"
            >
              Zarejestruj się
            </Link>
          </p>
        </div>
      </div>

      {/* Footer for login page */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 px-4 py-2">
        <div className="flex justify-center">
          <div className="text-xs text-gray-500">
            Wersja: <span className="font-medium">{versionData.current}</span>
          </div>
        </div>
        <div className="text-xs text-gray-400 mt-2 text-center">
          <Link to="/terms" className="hover:text-gray-600">
            Regulamin
          </Link>
          {' | '}
          <Link to="/privacy" className="hover:text-gray-600">
            Polityka Prywatności
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Login;
